/**
 * Incarcarea unei resurse de la core, cu cele trei stari facute explicite.
 *
 * Nu exista stare "gata, dar goala" pe eroare: `error` si `data` nu sunt
 * niciodata setate impreuna, iar un esec nu lasa in urma datele vechi. Altfel o
 * pagina care a incarcat o data meniul l-ar continua sa il arate dupa ce
 * serverul a inceput sa raspunda 500, iar clientul ar comanda din el.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../api/client";

export type Loadable<T> =
  | { state: "loading"; data: null; error: null }
  | { state: "ready"; data: T; error: null }
  | { state: "error"; data: null; error: ApiError };

const LOADING: Loadable<never> = { state: "loading", data: null, error: null };

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  return new ApiError({
    status: 0,
    code: "UNEXPECTED",
    message: error instanceof Error ? error.message : "Aplicația a întâmpinat o eroare neașteptată.",
  });
}

export interface UseLoadableResult<T> {
  state: Loadable<T>["state"];
  data: T | null;
  error: ApiError | null;
  reload: () => void;
}

export interface UseLoadableOptions {
  /**
   * La reincarcarea ceruta explicit (`reload`), pastreaza datele afisate cat
   * timp cererea e in zbor. Necesar la interogarea periodica: fara asta, pagina
   * de urmarire a comenzii ar reveni la ecranul de incarcare la fiecare ciclu,
   * iar clientul ar vedea starea comenzii disparand din 20 in 20 de secunde.
   *
   * Nu slabeste regula de mai sus: pe EROARE datele vechi tot se sterg.
   */
  keepDataWhileReloading?: boolean;
}

/**
 * `deps` decide cand se reia cererea. Cererea in curs se anuleaza la schimbarea
 * dependintelor, ca un raspuns intarziat sa nu suprascrie unul mai nou.
 */
export function useLoadable<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  options: UseLoadableOptions = {},
): UseLoadableResult<T> {
  const [result, setResult] = useState<Loadable<T>>(LOADING as Loadable<T>);
  const [nonce, setNonce] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const keepData = options.keepDataWhileReloading === true;
  const keepDataRef = useRef(keepData);
  keepDataRef.current = keepData;
  const lastNonceRef = useRef(nonce);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    // Datele se pastreaza doar cand rularea vine din `reload` pe aceleasi
    // dependinte. Cand dependintele s-au schimbat (alta comanda, alta vitrina),
    // ce e pe ecran descrie alt obiect si trebuie sa dispara imediat.
    const isReload = lastNonceRef.current !== nonce;
    lastNonceRef.current = nonce;
    setResult((current) =>
      keepDataRef.current && isReload && current.state === "ready" ? current : (LOADING as Loadable<T>),
    );
    loaderRef
      .current(controller.signal)
      .then((data) => {
        if (!active) return;
        setResult({ state: "ready", data, error: null });
      })
      .catch((error: unknown) => {
        if (!active || controller.signal.aborted) return;
        setResult({ state: "error", data: null, error: toApiError(error) });
      });
    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { state: result.state, data: result.data, error: result.error, reload };
}
